import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  NgZone,
  OnInit,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  FormGroupName,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { AuthService } from '../../_services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ErrorHandlerService } from '../../_services/error-handler.service';

@Component({
  selector: 'app-login',
  imports: [RouterModule, FormsModule, ReactiveFormsModule, CommonModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit {
  loginForm: any = FormGroupName;
  isLoading: boolean = false;
  errorMessage: string = '';
  private _snackBar = inject(MatSnackBar);
  selectedCountry = { name: 'India', dial: '+91', code: 'IN', flag: '🇮🇳' };
  countries: any = [];
  dropdownOpen = false;
  searchQuery = '';
  showPassword = false;
  readonly slides = [0, 1, 2, 3];
  currentSlide = 0;
  private sliderInterval: any;
  private readonly SLIDE_DURATION_MS = 3500;
  phoneFocused: boolean = false;

  constructor(
    private _fb: FormBuilder,
    private _AS: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private el: ElementRef,
    private ngZone: NgZone,
  ) {}

  ngOnInit() {
    this.formControls();
    this.getCode();
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    if (returnUrl) {
      this._AS.setRedirectUrl(returnUrl);
    }
    if (isPlatformBrowser(this.platformId)) {
      this.startSlider();
    }
  }
  ngOnDestroy(): void {
    this.stopSlider();
  }

  private startSlider(): void {
    this.ngZone.runOutsideAngular(() => {
      this.sliderInterval = setInterval(() => {
        this.ngZone.run(() => {
          this.currentSlide = (this.currentSlide + 1) % this.slides.length;
        });
      }, this.SLIDE_DURATION_MS);
    });
  }

  private stopSlider(): void {
    if (this.sliderInterval) {
      clearInterval(this.sliderInterval);
      this.sliderInterval = null;
    }
  }

  goToSlide(index: number): void {
    this.currentSlide = index;
    this.stopSlider();
    this.startSlider();
  }

  async getCode() {
    await this._AS.getCountryCode().subscribe((res: any) => {
      this.countries = res;
      this.selectedCountry = this.countries.find((c: any) => c.code === 'IN')!;
      this.loginForm.patchValue({ country: this.selectedCountry });
    });
  }

  formControls() {
    this.loginForm = this._fb.group({
      phone: ['', [Validators.required, Validators.minLength(7)]],
      country: [''],
      // password: ['', [Validators.required, Validators.minLength(3)]]
    });
  }

  // ─── Phone / Email detection ─────────────────────────────────────────────

  isPhoneInput(): boolean {
    const val = this.loginForm.get('phone')?.value || '';
    return /^[\d\s\-()+]+$/.test(val) && !/[@.]/.test(val) && val.length > 0;
  }

  onAuthInput(event: Event) {
    const input = (event.target as HTMLInputElement).value;
    const isPhone = /^[\d\s\-()+]+$/.test(input) && !/[@.]/.test(input);

    if (isPhone && input.length > 0) {
      const stripped = input.replace(/^\+?\d{1,4}[-\s]?/, '');
      if (!input.startsWith(this.selectedCountry.dial)) {
        this.loginForm.patchValue({ phone: input }, { emitEvent: false });
      }
    }
  }

  // ─── Country dropdown ─────────────────────────────────────────────────────

  get filteredCountries() {
    const q = this.searchQuery.toLowerCase();
    return this.countries.filter(
      (c: any) => c.name.toLowerCase().includes(q) || c.dial.includes(q),
    );
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  selectCountry(country: any) {
    // swap old dial code for new one in the phone field
    const currentPhone: string = this.loginForm.get('phone')?.value || '';
    if (currentPhone.startsWith(this.selectedCountry.dial)) {
      const numberOnly = currentPhone.slice(this.selectedCountry.dial.length);
      this.loginForm.patchValue(
        { phone: `${numberOnly}` },
        { emitEvent: false },
      );
    }

    this.selectedCountry = country;
    this.dropdownOpen = false;
    this.searchQuery = '';
    this.loginForm.patchValue({ country });
  }

  @HostListener('document:click', ['$event'])
  onOutsideClick(e: Event) {
    if (!this.el.nativeElement.contains(e.target)) this.dropdownOpen = false;
  }

  // ─── Auth ─────────────────────────────────────────────────────────────────

  togglePassword() {
    this.showPassword = !this.showPassword;
  }

  loginUser() {
    if (this.loginForm.invalid) {
      this.errorMessage = 'Please fill in all required fields correctly.';
      return;
    }
    this.isLoading = true;
    this.errorMessage = '';
    let user = {
      ...this.loginForm.value,
      country: this.loginForm.value.phone.includes('@')
        ? null
        : this.loginForm.value.country,
    };
    this._AS.userLogin(user).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        if (res.message === 'SSR mode') return;
        if (res.status) {
          debugger;
          this._snackBar.open(
            res.message || 'OTP sent successfully!',
            'close',
            {
              duration: 3000,
              panelClass: ['success-snackbar'],
            },
          );
          this.router.navigate(['/otp'], { state: { ...res.data, authReturnUrl: this.router.url }, replaceUrl: true });
        } else {
          if (!res.status && res.data.route) {
            this.router.navigateByUrl(res.data.route, { state: { ...res.data, authReturnUrl: this.router.url }, replaceUrl: true });
            return;
          } else {
            this.errorMessage = res.message || 'Access denied.';
            this._snackBar.open(this.errorMessage, 'close', {
              duration: 3000,
              panelClass: ['error-snackbar'],
            });
          }
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMessage =
          error.status === false
            ? error.message
            : 'Login failed. Please check your credentials.';
        this._snackBar.open(this.errorMessage, 'close', {
          duration: 3000,
          panelClass: ['error-snackbar'],
        });
        this.errorHandler.handleError(error, 'Login');
      },
    });
  }
}
